import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboard, getAptekaDashboard, getCashFlowSummary, getDistrictDues, getPartnerRiskRanking, getActionRequired, getSmartAlerts, getCashFlowTrend, getDeliveryRiskStatus, toggleSupplyHold, getStockIntelligence, getStockActionItems, getTodayTarget, getDeliveryControlList, getP2pDueAlerts, getActionItemsEnhanced, logPartnerAction, getWizardStatus } from '@/lib/api';
import { useStateDistrict, StateDistrictFilter } from '@/components/StateDistrictFilter';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  TrendingUp, CreditCard, Package, AlertTriangle, Phone, MessageCircle, Clock,
  Warehouse, Target, Building2, Truck, RefreshCw,
  Calendar, Ban, CheckCircle2, Banknote, MapPin,
  ChevronRight, Eye, Zap, Activity, ShieldAlert,
  Boxes, Timer, Send, HandMetal, ArrowRightLeft, TrendingDown, Archive, PackageSearch,
  Crosshair, ShieldCheck, ShieldX, ShieldMinus, CalendarClock,
  PlusCircle, Bell, AlertCircle, Flame, CircleDollarSign, PhoneCall, MessageSquare,
  GitCompare
} from 'lucide-react';
import { CompareMonthsPanel } from '@/components/dashboard/CompareMonths';
import { CtInitPanel } from '@/components/ct/CtInitPanel';
import { CtVariancePanel } from '@/components/ct/CtVariancePanel';
import { MonthOpeningWidget } from '@/components/dashboard/MonthOpeningWidget';

const currentMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const fmt = (n) => n?.toLocaleString() ?? '0';

export default function DashboardPage() {
  const { user } = useAuth();
  return user?.role === 'apteka' ? <AptekaDashboard /> : <ActionCommandCenter />;
}

// ==================== ACTION COMMAND CENTER ====================
function ActionCommandCenter() {
  const navigate = useNavigate();
  const { countries, selectedCountry, setSelectedCountry, states, districts, selectedState, setSelectedState, selectedDistrict, setSelectedDistrict, scope } = useStateDistrict();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedRef = useRef(false);
  const [activeTab, setActiveTab] = useState('money');
  const [selectedMonth, setSelectedMonth] = useState(currentMonth());
  const [showCompare, setShowCompare] = useState(false);

  // Generate month options (past 12 + current + future 1)
  const monthOptions = (() => {
    const opts = []; const now = new Date();
    for (let i = -12; i <= 1; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i);
      opts.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return opts;
  })();

  // Money
  const [cashFlow, setCashFlow] = useState(null);
  const [cashTrend, setCashTrend] = useState(null);
  const [actions, setActions] = useState(null);
  const [smartAlerts, setSmartAlerts] = useState(null);
  const [districtDues, setDistrictDues] = useState(null);
  const [riskRanking, setRiskRanking] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  // Stock
  const [stockIntel, setStockIntel] = useState(null);
  const [stockActions, setStockActions] = useState(null);
  // Control
  const [todayTarget, setTodayTarget] = useState(null);
  const [enhancedActions, setEnhancedActions] = useState(null);
  const [deliveryControlList, setDeliveryControlList] = useState(null);
  const [p2pAlerts, setP2pAlerts] = useState(null);

  const fetchAllData = useCallback(async (isRefresh = false) => {
    if (!selectedState) return;
    if (!hasLoadedRef.current && !isRefresh) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    try {
      const st = selectedState !== 'all' ? selectedState : undefined;
      const dist = selectedDistrict && selectedDistrict !== 'all' ? selectedDistrict : undefined;
      const mo = selectedMonth || undefined;
      
      // Clear loading as soon as ANY call resolves
      let firstDone = false;
      const markLoaded = () => { if (!firstDone) { firstDone = true; setLoading(false); hasLoadedRef.current = true; } };
      
      // Fire ALL calls at once, update UI as each resolves
      const calls = [
        getCashFlowSummary(st, dist, mo).then(r => { setCashFlow(r.data); markLoaded(); }).catch(() => markLoaded()),
        getCashFlowTrend(st, mo).then(r => { setCashTrend(r.data); markLoaded(); }).catch(() => markLoaded()),
        getActionRequired(st, dist, 10, mo).then(r => setActions(r.data)).catch(() => {}),
        getSmartAlerts(st, 8, mo).then(r => setSmartAlerts(r.data)).catch(() => {}),
        getDistrictDues(st, mo).then(r => setDistrictDues(r.data)).catch(() => {}),
        getPartnerRiskRanking(st, dist, 10, mo).then(r => setRiskRanking(r.data)).catch(() => {}),
        getDeliveryRiskStatus(st, mo).then(r => setDeliveryStatus(r.data)).catch(() => {}),
        getStockIntelligence(st, dist, mo).then(r => setStockIntel(r.data)).catch(() => {}),
        getStockActionItems(st, dist, 10, mo).then(r => setStockActions(r.data)).catch(() => {}),
        getTodayTarget(st, dist, mo).then(r => setTodayTarget(r.data)).catch(() => {}),
        getActionItemsEnhanced(st, dist, 15, mo).then(r => setEnhancedActions(r.data)).catch(() => {}),
        getDeliveryControlList(st, dist, mo).then(r => setDeliveryControlList(r.data)).catch(() => {}),
        getP2pDueAlerts(st, mo).then(r => setP2pAlerts(r.data)).catch(() => {}),
      ];
      
      await Promise.all(calls);
      if (isRefresh) toast.success('Refreshed');
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); setRefreshing(false); hasLoadedRef.current = true; }
  }, [selectedState, selectedDistrict, selectedMonth]);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  const goToPartner = (id) => navigate(`/apteka-control?id=${id}`);
  const callPartner = (phone) => phone && window.open(`tel:${phone}`, '_self');
  const messagePartner = (phone, msg) => { if (!phone) return; window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(msg || 'Hello')}`, '_blank'); };
  const handleBlockPartner = async (id, name) => { try { await toggleSupplyHold(id, true, 'Blocked from dashboard'); toast.success(`Blocked ${name}`); fetchAllData(true); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const handleUnblockPartner = async (id, name) => { try { await toggleSupplyHold(id, false, ''); toast.success(`Unblocked ${name}`); fetchAllData(true); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const handleLogAction = async (aptekaId, action, note) => {
    try { await logPartnerAction(aptekaId, action, note); toast.success(`Logged: ${action}`); } catch { /* silent */ }
  };
  const handleCardClick = (type) => {
    switch (type) {
      case 'collected': navigate('/payments'); break;
      case 'pending': case 'overdue': document.getElementById('action-required-section')?.scrollIntoView({ behavior: 'smooth' }); break;
      case 'exposure': navigate('/warehouse'); break;
      default: break;
    }
  };

  const tabs = [
    { key: 'money', label: 'Money', icon: Banknote, activeClass: 'bg-background shadow-md text-emerald-700 dark:text-emerald-400' },
    { key: 'stock', label: 'Stock', icon: Boxes, activeClass: 'bg-background shadow-md text-blue-700 dark:text-blue-400' },
    { key: 'control', label: 'Control', icon: Crosshair, activeClass: 'bg-background shadow-md text-rose-700 dark:text-rose-400' },
  ];

  return (
    <div className="max-w-[1920px] mx-auto" data-testid="command-center">
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b -mx-3 sm:-mx-4 md:-mx-6 px-3 sm:px-4 md:px-6 py-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-sm sm:text-base font-bold tracking-tight shrink-0">Command Center</h1>
          <div className="h-4 w-px bg-border hidden sm:block" />
          <StateDistrictFilter countries={countries} selectedCountry={selectedCountry} setSelectedCountry={setSelectedCountry} states={states} districts={districts} selectedState={selectedState} setSelectedState={setSelectedState} selectedDistrict={selectedDistrict} setSelectedDistrict={setSelectedDistrict} scope={scope} />
          <div className="ml-auto flex items-center gap-1.5 shrink-0">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="h-7 w-[100px] text-xs shrink-0" data-testid="month-picker">
                <Calendar className="w-3 h-3 mr-1 opacity-60" />
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(m => {
                  const [y, mo] = m.split('-');
                  const label = new Date(+y, +mo - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                  const isCurrent = m === currentMonth();
                  return <SelectItem key={m} value={m}><span className={isCurrent ? 'font-bold' : ''}>{label}{isCurrent ? ' (now)' : ''}</span></SelectItem>;
                })}
              </SelectContent>
            </Select>
            <Button onClick={() => fetchAllData(true)} size="sm" variant="outline" disabled={refreshing} className="h-7 px-2 shrink-0" data-testid="refresh-btn"><RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /></Button>
            <Button onClick={() => setShowCompare(!showCompare)} size="sm" variant={showCompare ? "default" : "outline"} className="h-7 px-2 text-xs gap-1 shrink-0" data-testid="compare-btn">
              <GitCompare className="w-3 h-3" /> <span className="hidden sm:inline">Compare</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 md:p-6 space-y-4">

      <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/60 border w-fit" data-testid="dashboard-tabs">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.key ? tab.activeClass : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${tab.key}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {loading && !hasLoadedRef.current && <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}

      {showCompare && (
        <CompareMonthsPanel
          monthOptions={monthOptions}
          state={selectedState}
          district={selectedDistrict}
          onClose={() => setShowCompare(false)}
        />
      )}

      {/* CT Initialization Tracker — visible on dashboard */}
      {selectedState && selectedState !== 'all' && (
        <CtInitPanel month={selectedMonth} state={selectedState} district={selectedDistrict} compact />
      )}

      {/* CT Variance Alerts — stock variance tracking */}
      {selectedState && selectedState !== 'all' && (
        <CtVariancePanel month={selectedMonth} state={selectedState} district={selectedDistrict} />
      )}

      {/* Month Opening Wizard Status Widget */}
      {selectedState && selectedState !== 'all' && (
        <MonthOpeningWidget month={selectedMonth} state={selectedState} district={selectedDistrict} />
      )}

      {/* Subtle refresh indicator - doesn't hide content */}
      {refreshing && <div className="fixed top-16 right-4 z-50 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium shadow-lg flex items-center gap-2"><RefreshCw className="w-3 h-3 animate-spin" />Updating...</div>}

      {(!loading || hasLoadedRef.current) && activeTab === 'money' && (
        <MoneyTab cashFlow={cashFlow} cashTrend={cashTrend} actions={actions} smartAlerts={smartAlerts} districtDues={districtDues} riskRanking={riskRanking} deliveryStatus={deliveryStatus} handleCardClick={handleCardClick} callPartner={callPartner} messagePartner={messagePartner} handleBlockPartner={handleBlockPartner} goToPartner={goToPartner} navigate={navigate} />
      )}
      {(!loading || hasLoadedRef.current) && activeTab === 'stock' && (
        <StockTab stockIntel={stockIntel} stockActions={stockActions} goToPartner={goToPartner} navigate={navigate} />
      )}
      {(!loading || hasLoadedRef.current) && activeTab === 'control' && (
        <ControlTab todayTarget={todayTarget} enhancedActions={enhancedActions} riskRanking={riskRanking} deliveryStatus={deliveryStatus} deliveryControlList={deliveryControlList} p2pAlerts={p2pAlerts} districtDues={districtDues} smartAlerts={smartAlerts} callPartner={callPartner} messagePartner={messagePartner} handleBlockPartner={handleBlockPartner} handleUnblockPartner={handleUnblockPartner} goToPartner={goToPartner} navigate={navigate} handleLogAction={handleLogAction} fetchAllData={fetchAllData} />
      )}
      </div>
    </div>
  );
}

// ==================== CONTROL TAB (MONEY COLLECTION MACHINE) ====================
function ControlTab({ todayTarget, enhancedActions, riskRanking, deliveryStatus, deliveryControlList, p2pAlerts, districtDues, smartAlerts, callPartner, messagePartner, handleBlockPartner, handleUnblockPartner, goToPartner, navigate, handleLogAction, fetchAllData }) {
  return (
    <div className="space-y-4" data-testid="control-tab">
      {/* ============ TODAY TARGET BLOCK ============ */}
      {todayTarget && <TodayTargetBlock data={todayTarget} />}

      {/* ============ CONTEXT QUICK ACTIONS ============ */}
      {todayTarget && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="quick-actions">
          {todayTarget.top_focus && (
            <button onClick={() => {
              callPartner(todayTarget.top_focus.phone);
              handleLogAction(todayTarget.top_focus.apteka_id, 'called', 'Called from Control tab');
            }} className="flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-300 font-semibold text-sm transition-all hover:shadow-md active:scale-[0.97]" data-testid="qa-collect">
              <CircleDollarSign className="w-5 h-5" />
              <div className="text-left">
                <p className="font-bold">Collect {fmt(todayTarget.top_focus.amount)}</p>
                <p className="text-[10px] opacity-70">{todayTarget.top_focus.name} | {todayTarget.top_focus.days_overdue}d overdue</p>
              </div>
            </button>
          )}
          {todayTarget.commitment_focus && (
            <button onClick={() => {
              callPartner(todayTarget.commitment_focus.phone);
              handleLogAction(todayTarget.commitment_focus.apteka_id, 'called', 'Commitment follow-up');
            }} className="flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/20 text-blue-800 dark:text-blue-300 font-semibold text-sm transition-all hover:shadow-md active:scale-[0.97]" data-testid="qa-call">
              <PhoneCall className="w-5 h-5" />
              <div className="text-left">
                <p className="font-bold">Call {todayTarget.commitment_focus.name}</p>
                <p className="text-[10px] opacity-70">Commitment due today</p>
              </div>
            </button>
          )}
          {todayTarget.block_focus && (
            <button onClick={() => handleBlockPartner(todayTarget.block_focus.apteka_id, todayTarget.block_focus.name)}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/20 text-rose-800 dark:text-rose-300 font-semibold text-sm transition-all hover:shadow-md active:scale-[0.97]" data-testid="qa-block">
              <Ban className="w-5 h-5" />
              <div className="text-left">
                <p className="font-bold">Block {todayTarget.block_focus.name}</p>
                <p className="text-[10px] opacity-70">Exceeds limit | TJS {fmt(todayTarget.block_focus.unpaid)}</p>
              </div>
            </button>
          )}
          {!todayTarget.commitment_focus && !todayTarget.block_focus && todayTarget.calls_needed > 0 && (
            <button onClick={() => { const p = riskRanking?.partners?.[0]; if (p?.phone) { callPartner(p.phone); handleLogAction(p.apteka_id, 'called', 'From Control'); }}}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300 font-semibold text-sm transition-all hover:shadow-md active:scale-[0.97]" data-testid="qa-reminder">
              <Bell className="w-5 h-5" />
              <div className="text-left">
                <p className="font-bold">Send Reminders</p>
                <p className="text-[10px] opacity-70">{todayTarget.calls_needed} partners need calls</p>
              </div>
            </button>
          )}
        </div>
      )}

      {/* ============ ENHANCED ACTION REQUIRED ============ */}
      {enhancedActions?.actions?.length > 0 && (
        <div id="action-required-section" className="rounded-xl border-2 border-rose-300 dark:border-rose-700 bg-card overflow-hidden" data-testid="action-required">
          <div className="bg-rose-50 dark:bg-rose-950/30 px-4 py-2.5 flex items-center justify-between border-b border-rose-200 dark:border-rose-800">
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-rose-600" />
              <span className="text-sm font-bold text-rose-700 dark:text-rose-300">ACTION REQUIRED</span>
              {enhancedActions.counts.critical > 0 && <Badge variant="destructive" className="text-[9px] h-4">{enhancedActions.counts.critical} critical</Badge>}
              {enhancedActions.counts.high > 0 && <Badge className="text-[9px] h-4 bg-amber-500">{enhancedActions.counts.high} high</Badge>}
              {enhancedActions.counts.medium > 0 && <Badge variant="secondary" className="text-[9px] h-4">{enhancedActions.counts.medium} medium</Badge>}
            </div>
            <span className="text-[10px] text-muted-foreground font-medium">{enhancedActions.total_count} total | TJS {fmt(enhancedActions.total_overdue)} overdue</span>
          </div>
          <div className="divide-y max-h-[420px] overflow-y-auto">
            {enhancedActions.actions.map((a, i) => (
              <EnhancedActionRow key={i} action={a} totalOverdue={enhancedActions.total_overdue}
                onCall={(phone) => { callPartner(phone); handleLogAction(a.apteka_id, 'called', a.action_text); }}
                onMessage={(phone) => { messagePartner(phone, `Payment reminder: TJS ${fmt(a.amount)} overdue`); handleLogAction(a.apteka_id, 'messaged', a.action_text); }}
                onCollect={() => { navigate('/payments'); handleLogAction(a.apteka_id, 'collected', `TJS ${fmt(a.amount)}`); }}
                onBlock={() => handleBlockPartner(a.apteka_id, a.apteka_name)}
                onView={() => goToPartner(a.apteka_id)} />
            ))}
          </div>
        </div>
      )}

      {/* ============ MISSED COMMITMENTS ============ */}
      <MissedCommitmentsBlock p2pAlerts={p2pAlerts} callPartner={callPartner} messagePartner={messagePartner} goToPartner={goToPartner} handleLogAction={handleLogAction} />

      {/* ============ PRIORITY PARTNERS + DELIVERY CONTROL ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <PriorityCollectionBlock ranking={riskRanking} callPartner={callPartner} messagePartner={messagePartner} goToPartner={goToPartner} handleLogAction={handleLogAction} />
        <DeliveryControlBlock list={deliveryControlList} deliveryStatus={deliveryStatus} onBlock={handleBlockPartner} onUnblock={handleUnblockPartner} goToPartner={goToPartner} />
      </div>

      {/* ============ DISTRICT RISK + INTELLIGENCE FEED ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <DistrictRiskBlock districtDues={districtDues} navigate={navigate} />
        <AlertsFeed alerts={smartAlerts} />
      </div>
    </div>
  );
}

// ==================== TODAY TARGET BLOCK ====================
function TodayTargetBlock({ data }) {
  const pct = data.progress_pct;
  const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-rose-500';

  return (
    <div className="rounded-xl border-2 border-slate-900 dark:border-slate-100 bg-slate-950 dark:bg-slate-50 text-white dark:text-slate-900 overflow-hidden" data-testid="today-target">
      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-400 dark:text-orange-600" />
            <span className="text-base font-black tracking-wide uppercase">Today Target</span>
          </div>
          {data.yesterday_collected > 0 && (
            <span className="text-[10px] opacity-50">Yesterday: TJS {fmt(data.yesterday_collected)}</span>
          )}
        </div>

        {/* Target / Collected / Remaining */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider opacity-50 font-semibold">Collect</p>
            <p className="text-lg sm:text-2xl font-black tabular-nums">{fmt(data.target)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider opacity-50 font-semibold">Done</p>
            <p className="text-2xl font-black tabular-nums text-emerald-400 dark:text-emerald-600">{fmt(data.collected)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider opacity-50 font-semibold">Remaining</p>
            <p className="text-2xl font-black tabular-nums text-rose-400 dark:text-rose-600">{fmt(data.remaining)}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-3 rounded-full bg-white/10 dark:bg-slate-900/10 overflow-hidden mb-3" data-testid="target-progress">
          <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center justify-between text-[10px] opacity-60">
          <span>{pct}% complete</span>
          <span>Total overdue: TJS {fmt(data.total_overdue)}</span>
        </div>

        {/* Action Summary Strip */}
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/10 dark:border-slate-900/10">
          {data.calls_needed > 0 && (
            <span className="text-[10px] px-2.5 py-1 rounded-full bg-blue-500/20 text-blue-300 dark:text-blue-700 font-bold">
              {data.calls_needed} calls needed
            </span>
          )}
          {data.commitments_today > 0 && (
            <span className="text-[10px] px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 dark:text-amber-700 font-bold">
              {data.commitments_today} commitments today
            </span>
          )}
          {data.blocked_count > 0 && (
            <span className="text-[10px] px-2.5 py-1 rounded-full bg-rose-500/20 text-rose-300 dark:text-rose-700 font-bold">
              {data.blocked_count} blocked
            </span>
          )}
          {data.top_focus && (
            <span className="text-[10px] px-2.5 py-1 rounded-full bg-orange-500/20 text-orange-300 dark:text-orange-700 font-bold ml-auto">
              Top: {data.top_focus.name} (TJS {fmt(data.top_focus.amount)} | {data.top_focus.impact_pct}% of overdue)
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== ENHANCED ACTION ROW ====================
function EnhancedActionRow({ action, totalOverdue, onCall, onMessage, onCollect, onBlock, onView }) {
  const bgMap = { 1: 'bg-rose-50 dark:bg-rose-950/20', 2: 'bg-amber-50/60 dark:bg-amber-950/10', 3: 'bg-muted/20' };
  const borderMap = { 'BLOCKED': 'border-l-rose-600', 'BLOCK RISK': 'border-l-orange-500', 'WARNING': 'border-l-amber-500', 'REMINDER': 'border-l-blue-400', 'DUE TODAY': 'border-l-blue-500' };
  const escalColorMap = { rose: 'bg-rose-600 text-white', orange: 'bg-orange-500 text-white', amber: 'bg-amber-500 text-white', blue: 'bg-blue-500 text-white' };

  return (
    <div className={`px-3 py-3 border-l-4 ${borderMap[action.escalation] || 'border-l-slate-300'} ${bgMap[action.priority] || bgMap[3]}`} data-testid="action-row">
      {/* Top row: escalation + name + impact */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={`text-[8px] px-1.5 py-0.5 rounded font-black tracking-wider ${escalColorMap[action.escalation_color] || 'bg-slate-500 text-white'}`}>{action.escalation}</span>
          <span className="text-sm font-bold truncate">{action.apteka_name}</span>
          <span className="text-[10px] text-muted-foreground">{action.district}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {action.impact_pct > 0 && (
            <span className="text-[10px] font-bold text-rose-600 tabular-nums">{action.impact_pct}% of overdue</span>
          )}
        </div>
      </div>

      {/* Amount + days */}
      <div className="flex items-center gap-3 mb-2">
        <span className="text-lg font-black text-rose-700 dark:text-rose-400 tabular-nums">TJS {fmt(action.amount)}</span>
        {action.days_overdue > 0 && (
          <span className="text-xs text-muted-foreground">{action.days_overdue} days overdue</span>
        )}
        {action.note && <span className="text-xs text-blue-600 italic">"{action.note}"</span>}
      </div>

      {/* Progress + Last Action */}
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {[0, 50, 100].map(step => (
              <div key={step} className={`w-5 h-1.5 rounded-full ${action.progress >= step ? (step === 100 ? 'bg-emerald-500' : step === 50 ? 'bg-amber-500' : 'bg-rose-400') : 'bg-muted'}`} />
            ))}
          </div>
          <span className="text-[9px] text-muted-foreground">
            {action.progress === 100 ? 'Paid today' : action.progress === 50 ? 'Action taken' : 'No action'}
          </span>
        </div>
        {action.last_action && (
          <span className="text-[9px] text-muted-foreground italic">
            Last: {action.last_action.action.replace('_', ' ')} by {action.last_action.user_name?.split(' ')[0] || 'admin'}
          </span>
        )}
      </div>

      {/* ACTION BUTTONS — text labels, not just icons */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {action.phone && (
          <Button size="sm" variant="outline" className="h-7 text-[11px] px-2.5 font-bold gap-1 border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => onCall(action.phone)} data-testid="action-call">
            <Phone className="w-3 h-3" /> Call
          </Button>
        )}
        {action.phone && (
          <Button size="sm" variant="outline" className="h-7 text-[11px] px-2.5 font-bold gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => onMessage(action.phone)} data-testid="action-message">
            <MessageSquare className="w-3 h-3" /> Message
          </Button>
        )}
        {action.suggested_actions?.includes('collect') && (
          <Button size="sm" variant="outline" className="h-7 text-[11px] px-2.5 font-bold gap-1 border-emerald-500 text-emerald-700 hover:bg-emerald-50 bg-emerald-50/50" onClick={onCollect} data-testid="action-collect">
            <CircleDollarSign className="w-3 h-3" /> Collect
          </Button>
        )}
        {action.suggested_actions?.includes('block') && (
          <Button size="sm" variant="outline" className="h-7 text-[11px] px-2.5 font-bold gap-1 border-rose-400 text-rose-700 hover:bg-rose-50" onClick={onBlock} data-testid="action-block">
            <Ban className="w-3 h-3" /> Block
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2.5 font-bold gap-1" onClick={onView} data-testid="action-view">
          <Eye className="w-3 h-3" /> View
        </Button>
      </div>
    </div>
  );
}

// ==================== MISSED COMMITMENTS ====================
function MissedCommitmentsBlock({ p2pAlerts, callPartner, messagePartner, goToPartner, handleLogAction }) {
  if (!p2pAlerts) return null;
  const due = p2pAlerts.due || [];
  const upcoming = p2pAlerts.upcoming || [];
  if (!due.length && !upcoming.length) return null;

  return (
    <div className="rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-card overflow-hidden" data-testid="missed-commitments">
      <div className="bg-amber-50 dark:bg-amber-950/30 px-4 py-2.5 flex items-center justify-between border-b border-amber-200 dark:border-amber-800">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-bold text-amber-700 dark:text-amber-300">MISSED COMMITMENTS</span>
          {due.length > 0 && <Badge variant="destructive" className="text-[9px] h-4">{due.length} missed/due</Badge>}
        </div>
      </div>
      <div className="divide-y max-h-[220px] overflow-y-auto">
        {due.map((p, i) => (
          <div key={`d-${i}`} className={`px-3 py-2.5 flex items-center justify-between gap-2 border-l-4 ${p.status === 'due_today' ? 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/10' : 'border-l-rose-500 bg-rose-50/50 dark:bg-rose-950/10'}`} data-testid={`commitment-due-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className={`text-[8px] px-1.5 py-0.5 rounded font-black ${p.status === 'due_today' ? 'bg-blue-500 text-white' : 'bg-rose-600 text-white'}`}>
                  {p.status === 'due_today' ? 'DUE TODAY' : `${p.days_overdue}d OVERDUE`}
                </span>
                <span className="text-xs font-bold truncate">{p.apteka_name}</span>
                <span className="text-[10px] text-muted-foreground">{p.district}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">{p.note || 'Payment promise'} | {p.promise_date}</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {p.phone && <>
                <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 font-bold gap-1" onClick={() => { callPartner(p.phone); handleLogAction(p.apteka_id, 'called', 'Commitment follow-up'); }} data-testid="commitment-call"><Phone className="w-3 h-3" /> Call</Button>
                <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 font-bold gap-1" onClick={() => { messagePartner(p.phone, `Reminder: ${p.note || 'Payment commitment'}`); handleLogAction(p.apteka_id, 'messaged', 'Commitment reminder'); }} data-testid="commitment-message"><MessageSquare className="w-3 h-3" /> Message</Button>
              </>}
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => goToPartner(p.apteka_id)} data-testid="commitment-view"><Eye className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
        {upcoming.map((p, i) => (
          <div key={`u-${i}`} className="px-3 py-2.5 flex items-center justify-between gap-2 bg-muted/20 border-l-4 border-l-slate-300" data-testid={`commitment-upcoming-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2"><Badge variant="secondary" className="text-[8px] h-4">IN {p.days_until}d</Badge><span className="text-xs font-bold truncate">{p.apteka_name}</span></div>
              <p className="text-[10px] text-muted-foreground mt-0.5">{p.note || 'Upcoming'} | {p.promise_date}</p>
            </div>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => goToPartner(p.apteka_id)}><ChevronRight className="w-3 h-3" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== MONEY TAB ====================
// Skeleton placeholder for loading sections
function SectionSkeleton({ rows = 3 }) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden animate-pulse">
      <div className="bg-muted/30 px-4 py-2.5 border-b"><div className="h-4 w-32 bg-muted rounded" /></div>
      <div className="divide-y">{Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="px-3 py-3 flex items-center gap-3"><div className="h-3 w-3 bg-muted rounded-full" /><div className="flex-1 space-y-1.5"><div className="h-3 bg-muted rounded w-3/4" /><div className="h-2 bg-muted rounded w-1/2" /></div><div className="h-4 w-16 bg-muted rounded" /></div>
      ))}</div>
    </div>
  );
}

function CardsSkeleton({ count = 6 }) {
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-${count} gap-2 sm:gap-3 animate-pulse`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border p-3 sm:p-4 space-y-2"><div className="h-3 w-16 bg-muted rounded" /><div className="h-6 w-24 bg-muted rounded" /><div className="h-2 w-12 bg-muted rounded" /></div>
      ))}
    </div>
  );
}

function MoneyTab({ cashFlow, cashTrend, actions, smartAlerts, districtDues, riskRanking, deliveryStatus, handleCardClick, callPartner, messagePartner, handleBlockPartner, goToPartner, navigate }) {
  return (
    <div className="space-y-4" data-testid="money-tab">
      {deliveryStatus && <DeliveryStatusBar status={deliveryStatus} />}
      {cashFlow ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
          <MetricCard icon={CheckCircle2} label="Collected" value={cashFlow.month_collections} subValue={`${cashFlow.payments_month_count} payments`} color="emerald" onClick={() => handleCardClick('collected')} testId="card-collected" />
          <MetricCard icon={Clock} label="Pending" value={cashFlow.pending_dues} color="amber" onClick={() => handleCardClick('pending')} testId="card-pending" />
          <MetricCard icon={AlertTriangle} label="Overdue" value={cashFlow.overdue_amount} color="rose" onClick={() => handleCardClick('overdue')} testId="card-overdue" />
          <MetricCard icon={Package} label="Exposure" value={cashFlow.stock_exposure} subValue="Unsold stock" color="orange" onClick={() => handleCardClick('exposure')} testId="card-exposure" />
          <MetricCard icon={Target} label="Collection" value={`${cashFlow.collection_efficiency}%`} color={cashFlow.collection_efficiency >= 70 ? 'emerald' : cashFlow.collection_efficiency >= 40 ? 'amber' : 'rose'} prefix="" testId="card-efficiency" />
          <MetricCard icon={Banknote} label="Payable" value={cashFlow.total_payable} color="blue" testId="card-payable" />
        </div>
      ) : <CardsSkeleton count={6} />}
      {cashTrend ? <TrendRow data={cashTrend} /> : <div className="h-20 rounded-lg border bg-card animate-pulse" />}
      {actions?.actions?.length > 0 ? <ActionBlock actions={actions} callPartner={callPartner} messagePartner={messagePartner} handleBlockPartner={handleBlockPartner} goToPartner={goToPartner} /> : !actions && <SectionSkeleton rows={4} />}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {smartAlerts ? <AlertsFeed alerts={smartAlerts} /> : <SectionSkeleton rows={3} />}
        {riskRanking ? <PriorityCollectionBlock ranking={riskRanking} callPartner={callPartner} messagePartner={messagePartner} goToPartner={goToPartner} /> : <SectionSkeleton rows={3} />}
      </div>
      {districtDues ? <DistrictRiskBlock districtDues={districtDues} navigate={navigate} /> : <SectionSkeleton rows={2} />}
    </div>
  );
}

// ==================== STOCK TAB ====================
function StockTab({ stockIntel, stockActions, goToPartner, navigate }) {
  if (!stockIntel) return <div className="space-y-4"><CardsSkeleton count={5} /><SectionSkeleton rows={3} /></div>;
  return (
    <div className="space-y-4" data-testid="stock-tab">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
        <MetricCard icon={Warehouse} label="Warehouse" value={stockIntel.warehouse_total_value} subValue={`${fmt(stockIntel.warehouse_total_qty)} units`} color="blue" onClick={() => navigate('/warehouse')} testId="card-warehouse-value" />
        <MetricCard icon={Package} label="At Partners" value={stockIntel.unsold_at_partners_value} subValue={`${fmt(stockIntel.unsold_at_partners_units)} units`} color="orange" testId="card-unsold-partners" />
        <MetricCard icon={Archive} label="Dead Stock" value={stockIntel.dead_stock_value} subValue={`${stockIntel.dead_stock_count} items`} color="rose" testId="card-dead-stock" />
        <MetricCard icon={Timer} label="Expiring" value={stockIntel.expiring_soon_count} subValue="Within 60 days" color="amber" prefix="" testId="card-expiring" />
        <MetricCard icon={TrendingDown} label="Low Stock" value={stockIntel.low_stock_product_count} subValue="Products shortage" color="rose" prefix="" testId="card-low-stock" />
      </div>
      <div className="rounded-lg border bg-card p-3 flex items-center justify-between" data-testid="stock-total-bar">
        <div className="flex items-center gap-3"><Boxes className="w-5 h-5 text-primary" /><div><p className="text-sm font-semibold">Total Stock Value</p><p className="text-[10px] text-muted-foreground">Warehouse + Partners</p></div></div>
        <div className="flex items-center gap-6">
          <div className="text-right"><p className="text-[10px] text-muted-foreground">Warehouse</p><p className="text-sm font-bold text-blue-600 tabular-nums">TJS {fmt(stockIntel.warehouse_total_value)}</p></div>
          <span className="text-muted-foreground">+</span>
          <div className="text-right"><p className="text-[10px] text-muted-foreground">At Partners</p><p className="text-sm font-bold text-orange-600 tabular-nums">TJS {fmt(stockIntel.unsold_at_partners_value)}</p></div>
          <span className="text-muted-foreground">=</span>
          <div className="text-right"><p className="text-[10px] text-muted-foreground">Total</p><p className="text-lg font-bold text-primary tabular-nums">TJS {fmt(stockIntel.total_stock_value)}</p></div>
        </div>
      </div>
      {stockActions?.actions?.length > 0 && (
        <div className="rounded-xl border-2 border-blue-300 dark:border-blue-700 bg-card overflow-hidden" data-testid="stock-action-required">
          <div className="bg-blue-50 dark:bg-blue-950/30 px-4 py-2.5 flex items-center justify-between border-b border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-blue-600" /><span className="text-sm font-bold text-blue-700 dark:text-blue-300">Stock Actions</span>{stockActions.counts.critical > 0 && <Badge variant="destructive" className="text-[9px] h-4">{stockActions.counts.critical} critical</Badge>}{stockActions.counts.high > 0 && <Badge className="text-[9px] h-4 bg-amber-500">{stockActions.counts.high} high</Badge>}</div>
            <span className="text-[10px] text-muted-foreground">{stockActions.total_count} total</span>
          </div>
          <div className="divide-y max-h-[320px] overflow-y-auto">{stockActions.actions.slice(0, 10).map((a, i) => <StockActionRow key={i} action={a} onView={goToPartner} onNavigate={navigate} />)}</div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <LowStockBlock products={stockIntel.low_stock_products} count={stockIntel.low_stock_product_count} />
        <ExpiringBlock items={stockIntel.expiring_soon} count={stockIntel.expiring_soon_count} />
      </div>
      <UnsoldPartnersBlock partners={stockIntel.top_unsold_partners} goToPartner={goToPartner} />
    </div>
  );
}

// ==================== SHARED COMPONENTS ====================
function DeliveryStatusBar({ status }) {
  const c = status.status === 'SAFE' ? 'emerald' : status.status === 'RESTRICTED' ? 'amber' : 'rose';
  return (
    <div className={`rounded-lg px-4 py-2.5 flex items-center justify-between bg-${c}-100 dark:bg-${c}-950/30 border border-${c}-300`} data-testid="delivery-status-bar">
      <div className="flex items-center gap-3"><div className={`w-3 h-3 rounded-full animate-pulse bg-${c}-500`} /><span className={`text-sm font-bold text-${c}-700 dark:text-${c}-300`}>Delivery Status: {status.status}</span></div>
      <div className="flex items-center gap-4 text-xs"><span className="text-emerald-700 dark:text-emerald-400">{status.safe} safe</span><span className="text-amber-700 dark:text-amber-400">{status.restricted} restricted</span><span className="text-rose-700 dark:text-rose-400">{status.blocked} blocked</span></div>
    </div>
  );
}

function TrendRow({ data }) {
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-3 p-3 rounded-lg border bg-card" data-testid="cash-trend">
      <div className="text-center"><p className="text-[10px] text-muted-foreground uppercase">Today</p><p className="text-lg sm:text-xl font-bold tabular-nums text-emerald-600">{fmt(data.today)}</p></div>
      <div className="text-center border-x"><p className="text-[10px] text-muted-foreground uppercase">Yesterday</p><p className="text-lg sm:text-xl font-bold tabular-nums">{fmt(data.yesterday)}</p></div>
      <div className="text-center"><p className="text-[10px] text-muted-foreground uppercase">7-Day Avg</p><p className="text-lg sm:text-xl font-bold tabular-nums text-blue-600">{fmt(data.avg_7_day)}</p></div>
    </div>
  );
}

function ActionBlock({ actions, callPartner, messagePartner, handleBlockPartner, goToPartner }) {
  return (
    <div id="action-required-section" className="rounded-xl border-2 border-rose-300 dark:border-rose-700 bg-card overflow-hidden" data-testid="action-required">
      <div className="bg-rose-50 dark:bg-rose-950/30 px-4 py-2.5 flex items-center justify-between border-b border-rose-200 dark:border-rose-800">
        <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-rose-600" /><span className="text-sm font-bold text-rose-700 dark:text-rose-300">Action Required</span>{actions.counts?.critical > 0 && <Badge variant="destructive" className="text-[9px] h-4">{actions.counts.critical} critical</Badge>}{actions.counts?.high > 0 && <Badge className="text-[9px] h-4 bg-amber-500">{actions.counts.high} high</Badge>}</div>
        <span className="text-[10px] text-muted-foreground">{actions.total_count} total</span>
      </div>
      <div className="divide-y max-h-[300px] overflow-y-auto">
        {actions.actions.slice(0, 10).map((a, i) => {
          const bg = { 1: 'bg-rose-100 dark:bg-rose-950/30 border-l-4 border-l-rose-500', 2: 'bg-amber-50 dark:bg-amber-950/20 border-l-4 border-l-amber-500', 3: 'bg-muted/30 border-l-4 border-l-blue-400' };
          return (
            <div key={i} className={`px-3 py-2.5 flex items-center justify-between gap-2 ${bg[a.priority] || bg[3]}`} data-testid="action-row">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><Badge variant={a.priority === 1 ? 'destructive' : a.priority === 2 ? 'default' : 'secondary'} className="text-[8px] h-4 shrink-0">{a.priority_label}</Badge><span className="text-xs font-semibold truncate">{a.apteka_name}</span><span className="text-[10px] text-muted-foreground">{a.district}</span></div>
                <p className="text-[10px] text-muted-foreground mt-0.5">{a.action_text}</p>
                {a.amount > 0 && <p className="text-xs font-bold text-rose-600 tabular-nums mt-0.5">TJS {fmt(a.amount)}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {a.phone && <><Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => callPartner(a.phone)} data-testid="action-call"><Phone className="w-3.5 h-3.5 text-primary" /></Button><Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => messagePartner(a.phone, `Follow up: ${a.reason}`)} data-testid="action-message"><MessageCircle className="w-3.5 h-3.5 text-emerald-600" /></Button></>}
                {a.suggested_actions?.includes('block') && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-500" onClick={() => handleBlockPartner(a.apteka_id, a.apteka_name)} data-testid="action-block"><Ban className="w-3.5 h-3.5" /></Button>}
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => goToPartner(a.apteka_id)} data-testid="action-view"><Eye className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AlertsFeed({ alerts }) {
  const iconMap = { check: CheckCircle2, ban: Ban, map: MapPin, alert: AlertTriangle, calendar: Calendar, 'credit-card': CreditCard };
  const colorMap = { emerald: 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 border-emerald-200', rose: 'bg-rose-50 dark:bg-rose-950/20 text-rose-600 border-rose-200', amber: 'bg-amber-50 dark:bg-amber-950/20 text-amber-600 border-amber-200', blue: 'bg-blue-50 dark:bg-blue-950/20 text-blue-600 border-blue-200', orange: 'bg-orange-50 dark:bg-orange-950/20 text-orange-600 border-orange-200' };
  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="smart-alerts">
      <div className="px-4 py-2.5 border-b bg-muted/30 flex items-center gap-2"><Activity className="w-4 h-4 text-primary" /><span className="text-sm font-semibold">Intelligence Feed</span></div>
      <div className="p-3 space-y-2 max-h-[220px] overflow-y-auto">
        {alerts?.alerts?.map((a, i) => { const Icon = iconMap[a.icon] || AlertCircle; return (
          <div key={i} className={`p-2.5 rounded-lg border ${colorMap[a.color] || colorMap.blue}`}>
            <div className="flex items-start gap-2"><Icon className="w-4 h-4 shrink-0 mt-0.5" /><div className="flex-1 min-w-0"><p className="text-xs font-semibold">{a.title}</p><p className="text-[10px] opacity-70 mt-0.5">{a.subtitle}</p></div></div>
          </div>
        ); })}
        {(!alerts?.alerts?.length) && <p className="text-xs text-muted-foreground text-center py-4">No alerts</p>}
      </div>
    </div>
  );
}

function PriorityCollectionBlock({ ranking, callPartner, messagePartner, goToPartner, handleLogAction }) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="priority-collection">
      <div className="px-4 py-2.5 border-b bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-rose-500" /><span className="text-sm font-semibold">Priority Collection</span></div>
        {ranking && <Badge variant="outline" className="text-[9px]">{ranking.high_risk_count} high risk</Badge>}
      </div>
      <div className="divide-y max-h-[220px] overflow-y-auto">
        {ranking?.partners?.slice(0, 6).map((p, i) => {
          const c = p.risk_score >= 60 ? 'rose' : p.risk_score >= 30 ? 'amber' : 'emerald';
          return (
            <div key={p.apteka_id} className="px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-muted/30" data-testid={`priority-${i}`}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold bg-${c}-100 text-${c}-700`}>{i + 1}</div>
                <div className="min-w-0"><p className="text-xs font-semibold truncate">{p.name}</p><p className="text-[9px] text-muted-foreground">{p.district} | {p.risk_factors?.[0] || 'Risk'}</p></div>
              </div>
              <p className="text-xs font-bold text-rose-600 tabular-nums shrink-0">TJS {fmt(p.pending_dues)}</p>
              <div className="flex items-center gap-0.5 shrink-0">
                {p.phone && <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { callPartner(p.phone); handleLogAction?.(p.apteka_id, 'called', 'From priority list'); }}><Phone className="w-3 h-3 text-primary" /></Button>}
                {p.phone && <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { messagePartner(p.phone, `Reminder: TJS ${fmt(p.pending_dues)} pending`); handleLogAction?.(p.apteka_id, 'messaged', 'From priority list'); }}><MessageCircle className="w-3 h-3 text-emerald-600" /></Button>}
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => goToPartner(p.apteka_id)}><ChevronRight className="w-3 h-3" /></Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DeliveryControlBlock({ list, deliveryStatus, onBlock, onUnblock, goToPartner }) {
  const [view, setView] = useState('blocked');
  if (!list) return null;
  const items = view === 'blocked' ? list.blocked : view === 'restricted' ? list.restricted : list.allowed;
  const counts = list.counts || {};
  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="delivery-control-block">
      <div className="px-4 py-2.5 border-b bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-rose-500" /><span className="text-sm font-semibold">Delivery Control</span></div>
        {deliveryStatus && <Badge variant={deliveryStatus.status === 'SAFE' ? 'secondary' : deliveryStatus.status === 'RESTRICTED' ? 'default' : 'destructive'} className="text-[9px]">{deliveryStatus.status}</Badge>}
      </div>
      <div className="flex border-b text-[10px]">
        {[{ key: 'blocked', label: 'Blocked', count: counts.blocked, icon: ShieldX, c: 'rose' }, { key: 'restricted', label: 'Restricted', count: counts.restricted, icon: ShieldMinus, c: 'amber' }, { key: 'allowed', label: 'Allowed', count: counts.allowed, icon: ShieldCheck, c: 'emerald' }].map(tab => (
          <button key={tab.key} onClick={() => setView(tab.key)} className={`flex-1 flex items-center justify-center gap-1.5 py-2 font-semibold transition-colors ${view === tab.key ? `text-${tab.c}-700 dark:text-${tab.c}-400 border-b-2 border-${tab.c}-500` : 'text-muted-foreground hover:text-foreground'}`} data-testid={`dc-tab-${tab.key}`}>
            <tab.icon className="w-3 h-3" />{tab.label} ({tab.count || 0})
          </button>
        ))}
      </div>
      <div className="divide-y max-h-[200px] overflow-y-auto">
        {items?.slice(0, 8).map((p, i) => (
          <div key={p.apteka_id} className="px-3 py-2 flex items-center justify-between gap-2 hover:bg-muted/30" data-testid={`dc-partner-${i}`}>
            <div className="flex-1 min-w-0"><p className="text-xs font-semibold truncate">{p.apteka_name}</p><p className="text-[9px] text-muted-foreground">{p.district} | {p.reason}</p></div>
            <div className="text-right shrink-0 mr-1"><p className="text-xs font-bold text-rose-600 tabular-nums">TJS {fmt(p.unpaid)}</p><p className="text-[9px] text-muted-foreground">{p.collection_pct}%</p></div>
            <div className="flex items-center gap-0.5 shrink-0">
              {p.status === 'blocked' ? <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[9px] text-emerald-600" onClick={() => onUnblock(p.apteka_id, p.apteka_name)} data-testid="dc-unblock"><CheckCircle2 className="w-3 h-3 mr-0.5" />Unblock</Button> : p.status !== 'allowed' ? <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[9px] text-rose-600" onClick={() => onBlock(p.apteka_id, p.apteka_name)} data-testid="dc-block"><Ban className="w-3 h-3 mr-0.5" />Block</Button> : null}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => goToPartner(p.apteka_id)}><ChevronRight className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
        {(!items || !items.length) && <p className="text-xs text-muted-foreground text-center py-4">None</p>}
      </div>
    </div>
  );
}

function DistrictRiskBlock({ districtDues, navigate }) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="district-risk">
      <div className="px-4 py-2.5 border-b bg-muted/30 flex items-center justify-between"><div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /><span className="text-sm font-semibold">District Risk</span></div>{districtDues && <span className="text-xs text-muted-foreground">{districtDues.total_districts} districts</span>}</div>
      <div className="divide-y max-h-[250px] overflow-y-auto">
        {districtDues?.districts?.slice(0, 6).map((d, i) => {
          const c = d.collection_pct >= 70 ? 'emerald' : d.collection_pct >= 40 ? 'amber' : 'rose';
          return (
            <button key={d.district} onClick={() => navigate(`/aptekas?district=${d.district}`)} className="w-full px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-muted/30 text-left" data-testid={`district-${i}`}>
              <div className="flex items-center gap-2 flex-1 min-w-0"><div className={`w-2 h-2 rounded-full bg-${c}-500`} /><span className="text-xs font-medium truncate">{d.district}</span><span className="text-[10px] text-muted-foreground">{d.partner_count} partners</span></div>
              <div className="flex items-center gap-3 shrink-0"><span className="text-xs font-bold text-rose-600 tabular-nums">TJS {fmt(d.pending_dues)}</span><span className={`text-xs font-bold text-${c}-600`}>{d.collection_pct}%</span><ChevronRight className="w-3 h-3 text-muted-foreground" /></div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StockActionRow({ action, onView, onNavigate }) {
  const cfg = { dead_stock: { icon: Archive, border: 'border-l-rose-500', bg: 'bg-rose-50 dark:bg-rose-950/20' }, expiring_stock: { icon: Timer, border: 'border-l-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/20' }, low_warehouse_stock: { icon: TrendingDown, border: 'border-l-rose-500', bg: 'bg-rose-100 dark:bg-rose-950/30' }, send_stock: { icon: Send, border: 'border-l-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/20' }, hold_supply: { icon: HandMetal, border: 'border-l-orange-500', bg: 'bg-orange-50 dark:bg-orange-950/20' } };
  const c = cfg[action.type] || { icon: AlertCircle, border: 'border-l-gray-400', bg: 'bg-muted/30' };
  const Icon = c.icon;
  return (
    <div className={`px-3 py-2.5 flex items-center justify-between gap-2 border-l-4 ${c.border} ${c.bg}`} data-testid="stock-action-row">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Icon className="w-4 h-4 shrink-0 opacity-70" />
        <div className="min-w-0">
          <div className="flex items-center gap-2"><Badge variant={action.priority === 1 ? 'destructive' : action.priority === 2 ? 'default' : 'secondary'} className="text-[8px] h-4 shrink-0">{action.priority_label}</Badge>{action.apteka_name && <span className="text-xs font-semibold truncate">{action.apteka_name}</span>}</div>
          <p className="text-[10px] text-muted-foreground mt-0.5">{action.action_text}</p>
          {action.value > 0 && <p className="text-xs font-bold text-rose-600 tabular-nums mt-0.5">TJS {fmt(action.value)}</p>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {action.suggested_actions?.includes('send_stock') && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => action.apteka_id ? onView(action.apteka_id) : onNavigate('/warehouse')} data-testid="stock-action-send"><Send className="w-3.5 h-3.5 text-blue-600" /></Button>}
        {action.suggested_actions?.includes('transfer') && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => action.apteka_id ? onView(action.apteka_id) : onNavigate('/warehouse')} data-testid="stock-action-transfer"><ArrowRightLeft className="w-3.5 h-3.5 text-purple-600" /></Button>}
        {action.suggested_actions?.includes('restock') && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onNavigate('/warehouse')} data-testid="stock-action-restock"><Truck className="w-3.5 h-3.5 text-emerald-600" /></Button>}
        {action.apteka_id && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onView(action.apteka_id)} data-testid="stock-action-view"><Eye className="w-3.5 h-3.5" /></Button>}
      </div>
    </div>
  );
}

function LowStockBlock({ products, count }) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="low-stock-products">
      <div className="px-4 py-2.5 border-b bg-muted/30 flex items-center justify-between"><div className="flex items-center gap-2"><TrendingDown className="w-4 h-4 text-rose-500" /><span className="text-sm font-semibold">Low Stock</span></div><Badge variant="outline" className="text-[9px]">{count}</Badge></div>
      <div className="divide-y max-h-[220px] overflow-y-auto">
        {products?.map((p, i) => <div key={p.product_id} className="px-3 py-2.5 flex items-center justify-between hover:bg-muted/30" data-testid={`low-stock-${i}`}><div className="flex-1 min-w-0"><p className="text-xs font-semibold truncate">{p.product_name}</p><p className="text-[9px] text-muted-foreground">{p.warehouse_qty} in stock | {p.avg_monthly_pr}/mo | {p.duration_months}mo</p></div><div className="text-right shrink-0"><p className="text-xs font-bold text-rose-600">Need {p.need}</p><p className="text-[9px] text-muted-foreground">TJS {fmt(p.value_needed)}</p></div></div>)}
        {(!products?.length) && <p className="text-xs text-muted-foreground text-center py-6">Well-stocked</p>}
      </div>
    </div>
  );
}

function ExpiringBlock({ items, count }) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="expiring-stock">
      <div className="px-4 py-2.5 border-b bg-muted/30 flex items-center justify-between"><div className="flex items-center gap-2"><Timer className="w-4 h-4 text-amber-500" /><span className="text-sm font-semibold">Expiring Soon</span></div><Badge variant="outline" className="text-[9px]">{count}</Badge></div>
      <div className="divide-y max-h-[220px] overflow-y-auto">
        {items?.map((p, i) => { const dl = Math.ceil((new Date(p.expiry_date) - new Date()) / 86400000); return <div key={i} className="px-3 py-2.5 flex items-center justify-between hover:bg-muted/30" data-testid={`expiring-${i}`}><div className="flex-1 min-w-0"><p className="text-xs font-semibold truncate">{p.product_name}</p><p className="text-[9px] text-muted-foreground">{p.quantity} units | Exp: {p.expiry_date}</p></div><div className="text-right shrink-0"><Badge variant={dl < 30 ? 'destructive' : 'default'} className="text-[9px] h-4">{dl}d</Badge><p className="text-[9px] text-muted-foreground mt-0.5">TJS {fmt(p.value)}</p></div></div>; })}
        {(!items?.length) && <p className="text-xs text-muted-foreground text-center py-6">None</p>}
      </div>
    </div>
  );
}

function UnsoldPartnersBlock({ partners, goToPartner }) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="top-unsold-partners">
      <div className="px-4 py-2.5 border-b bg-muted/30 flex items-center justify-between"><div className="flex items-center gap-2"><PackageSearch className="w-4 h-4 text-orange-500" /><span className="text-sm font-semibold">Top Unsold at Partners</span></div><span className="text-xs text-muted-foreground">{partners?.length || 0}</span></div>
      <div className="divide-y max-h-[250px] overflow-y-auto">
        {partners?.map((p, i) => <button key={p.apteka_id} onClick={() => goToPartner(p.apteka_id)} className="w-full px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-muted/30 text-left" data-testid={`unsold-partner-${i}`}><div className="flex items-center gap-2 flex-1 min-w-0"><div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i < 3 ? 'bg-orange-100 text-orange-700' : 'bg-muted text-muted-foreground'}`}>{i + 1}</div><div className="min-w-0"><p className="text-xs font-semibold truncate">{p.apteka_name}</p><p className="text-[9px] text-muted-foreground">{p.district} | {fmt(p.unsold_units)} units</p></div></div><div className="flex items-center gap-2 shrink-0"><span className="text-xs font-bold text-orange-600 tabular-nums">TJS {fmt(p.unsold_value)}</span><ChevronRight className="w-3 h-3 text-muted-foreground" /></div></button>)}
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, subValue, color = "blue", onClick, prefix = "TJS ", testId }) {
  const cls = { emerald: 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400', amber: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400', rose: 'bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400', orange: 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800 text-orange-600 dark:text-orange-400', blue: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400' };
  return (
    <button onClick={onClick} disabled={!onClick} className={`rounded-lg border p-3 text-left transition-all hover:shadow-md hover:scale-[1.02] active:scale-[0.98] ${cls[color]} ${onClick ? 'cursor-pointer' : ''}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2"><Icon className="w-4 h-4" />{onClick && <ChevronRight className="w-3 h-3 opacity-50" />}</div>
      <p className="text-[10px] uppercase font-medium opacity-70">{label}</p>
      <p className="text-lg sm:text-xl font-bold tabular-nums">{typeof value === 'number' ? `${prefix}${fmt(value)}` : value}</p>
      {subValue && <p className="text-[9px] opacity-60 mt-0.5">{subValue}</p>}
    </button>
  );
}

// ==================== APTEKA DASHBOARD ====================
function AptekaDashboard() {
  const { t } = useLanguage();
  const [selectedMonth, setSelectedMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const months = []; const now = new Date();
  for (let i = -6; i <= 6; i++) { const d = new Date(now.getFullYear(), now.getMonth() + i); months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); }
  const fetchData = useCallback(async () => { setLoading(true); try { const res = await getAptekaDashboard(selectedMonth); setData(res.data); } catch { toast.error('Failed'); } finally { setLoading(false); } }, [selectedMonth]);
  useEffect(() => { fetchData(); }, [fetchData]);
  return (
    <div className="p-6 space-y-6 max-w-[1920px] mx-auto" data-testid="apteka-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div><h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{data?.apteka ? data.apteka.name : t('dashboard')}</h1><p className="text-sm text-muted-foreground mt-1">Your pharmacy overview</p></div>
        <Select value={selectedMonth} onValueChange={setSelectedMonth}><SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="Month" /></SelectTrigger><SelectContent>{months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
      </div>
      {loading && <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}
      {data && !loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard icon={Building2} label="Companies" value={data.totals?.total_companies || 0} prefix="" color="blue" />
          <MetricCard icon={TrendingUp} label="Delivered" value={data.totals?.total_delivered || 0} color="emerald" />
          <MetricCard icon={CreditCard} label="Paid" value={data.totals?.total_paid || 0} color="emerald" />
          <MetricCard icon={AlertTriangle} label="Unpaid" value={data.totals?.total_unpaid || 0} color="rose" />
        </div>
      )}
    </div>
  );
}
